import {fromPromise, sendParent, setup, assign} from 'xstate';

import {EntityMapEntityNodeOrInaccessibleInfoNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {fetchEntityMapGraph} from './EntityMapDAO';
import {
  GraphNodesAndEdgesByIdWithFullyExpandedEntityIds,
  parseEntityMapIntoGraphNodesAndEdgesByIdWithFullyExpandedEntityIds,
} from './EntityMapUtils';

export interface EntityNodeInput {
  entityId: string;
  entity: EntityMapEntityNodeOrInaccessibleInfoNode;
  isFullyExpanded: boolean;
}

interface EntityNodeContext {
  entityId: string;
  entity: EntityMapEntityNodeOrInaccessibleInfoNode;
  isFullyExpanded: boolean;
}

interface EntityNodeEvents {
  type: 'loadExtendedGraph';
}

/**
 * Returns true if the entity can be expanded, i.e. it is accessible and has not already been fully expanded.
 */
function canEntityBeExpanded(context: EntityNodeContext): boolean {
  return !isInaccessible(context.entity) && !context.isFullyExpanded;
}

/**
 * XState state machine for managing an individual entity node within an entity map graph, for a given
 * entityId, and loading relationships from that entity.
 *
 * The idle state handles the loadExtendedGraph event, which can be sent from the view using actorRef.send().
 */
export const EntityNodeMachine = setup({
  types: {
    input: {} as EntityNodeInput,
    context: {} as EntityNodeContext,
    events: {} as EntityNodeEvents,
  },
  actors: {
    loadExtendedGraph: fromPromise<GraphNodesAndEdgesByIdWithFullyExpandedEntityIds, {entityId: string}>(
      async ({input: {entityId}}) => {
        const entityMapGraph = await fetchEntityMapGraph(entityId, 1);
        return parseEntityMapIntoGraphNodesAndEdgesByIdWithFullyExpandedEntityIds(entityMapGraph);
      }
    ),
  },
  actions: {
    // Extracted to a named action to make it easier to stub in tests using EntityNodeMachine.provide()
    notifyParent: sendParent((_, params: GraphNodesAndEdgesByIdWithFullyExpandedEntityIds) => ({
      type: 'completedFetchingExtendedGraph',
      graphNodesById: params.graphNodesById,
      graphEdgesById: params.graphEdgesById,
      fullyExpandedEntityIds: params.fullyExpandedEntityIds,
    })),
  },
}).createMachine({
  context: ({input}) => ({
    entityId: input.entityId,
    entity: input.entity,
    isFullyExpanded: input.isFullyExpanded,
  }),
  initial: 'checkIfInaccessible',
  states: {
    checkIfInaccessible: {
      always: [
        {
          target: 'idle',
          guard: ({context}) => canEntityBeExpanded(context),
        },
        {
          target: 'done',
          guard: ({context}) => !canEntityBeExpanded(context),
        },
      ],
    },
    idle: {
      on: {
        loadExtendedGraph: {
          target: 'loading',
        },
      },
    },
    loading: {
      invoke: {
        id: 'loadExtendedGraphActorFromLoadingState',
        input: ({context}) => ({
          entityId: context.entityId,
        }),
        src: 'loadExtendedGraph',
        onError: {
          target: 'error',
        },
        onDone: {
          target: 'done',
          actions: [
            assign({isFullyExpanded: true}),
            {
              type: 'notifyParent',
              params: ({event}) => event.output,
            },
          ],
        },
      },
    },
    error: {},
    done: {
      type: 'final',
    },
  },
});
